import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Infrastructure shared by the components: a class-name helper, the portal
 * container (overlays render inside the element that carries the tokens, so
 * they inherit direction, mode and density), and the layer stack that gives
 * Esc its precedence: it closes only the innermost open layer (menu, then
 * center peek, then side peek) and the layer returns focus to its trigger.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { DEFAULT_DIRECTION } from '../tokens/types.js';
import { decideDeferred, decideEscape, isEditableTarget } from './layers.js';
export function cx(...parts) {
    return parts.filter(Boolean).join(' ');
}
/* ------------------------------------------------------------------ */
/* portal container                                                    */
/* ------------------------------------------------------------------ */
const PortalContext = createContext(null);
export function PortalContainer({ element, children, }) {
    return _jsx(PortalContext.Provider, { value: element, children: children });
}
/** The element overlays are portaled into (the token root), or `undefined` for the body. */
export function usePortalContainer() {
    return useContext(PortalContext) ?? undefined;
}
/* ------------------------------------------------------------------ */
/* direction                                                           */
/* ------------------------------------------------------------------ */
const DirectionContext = createContext(DEFAULT_DIRECTION);
/**
 * The direction of the token root below it. Components that decide in script
 * whether a type has a hue of its own (a user type such as Deity has one in
 * Grimoire Noir only) read it; without a provider it is the default direction.
 */
export function DirectionProvider({ direction, children, }) {
    return _jsx(DirectionContext.Provider, { value: direction, children: children });
}
export function useDirection() {
    return useContext(DirectionContext);
}
/**
 * A token root inside another (UI r4 deliverable 48): a `div.cx-root` with its
 * own direction, mode and density, its own `DirectionProvider` and its own
 * `PortalContainer`, so a menu or popover opened inside portals into it and
 * takes its tokens. Run mode renders its whole page in one
 * `<TokenScope mode="dark" density="table">`, outside its panes' scroll
 * containers. It adds no layer stack: its layers register with the app's.
 */
export function TokenScope({ mode, density, direction = DEFAULT_DIRECTION, motion = 'system', className, testId, children, }) {
    const [element, setElement] = useState(null);
    return (_jsx("div", { ref: setElement, className: cx('cx-root', 'cx-token-scope', className), "data-direction": direction, "data-mode": mode, "data-density": density, "data-motion": motion, "data-testid": testId, children: _jsx(DirectionProvider, { direction: direction, children: _jsx(PortalContainer, { element: element, children: children }) }) }));
}
const LayerContext = createContext(null);
/**
 * Owns Escape for everything inside it: one press closes the innermost open
 * layer and nothing else. When the innermost layer is a peek (it registered
 * a region) and focus is in editable text, the text gets the Esc first and
 * only an Esc nothing consumed closes the peek (`layers.ts`). A provider
 * inside another adds nothing: its layers register with the outer one.
 */
export function LayerProvider({ children }) {
    const outer = useContext(LayerContext);
    const stack = useRef([]);
    useEffect(() => {
        if (outer)
            return;
        let deferred = null;
        const innermost = () => stack.current[stack.current.length - 1];
        const onCapture = (event) => {
            const top = innermost();
            const decision = decideEscape({
                key: event.key,
                isComposing: event.isComposing,
                keyCode: event.keyCode,
                hasLayer: top !== undefined,
                innermostHasRegion: top?.region !== undefined,
                targetEditable: isEditableTarget(event.target),
            });
            if (decision === 'ignore' || !top)
                return;
            if (decision === 'defer') {
                deferred = { event, entry: top };
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            top.close();
        };
        const onBubble = (event) => {
            if (!deferred || deferred.event !== event)
                return;
            const { entry } = deferred;
            deferred = null;
            if (decideDeferred({
                defaultPrevented: event.defaultPrevented,
                stillInnermost: innermost() === entry,
            })) {
                event.preventDefault();
                entry.close();
            }
        };
        window.addEventListener('keydown', onCapture, true);
        window.addEventListener('keydown', onBubble);
        return () => {
            window.removeEventListener('keydown', onCapture, true);
            window.removeEventListener('keydown', onBubble);
        };
    }, [outer]);
    const api = useMemo(() => ({
        register(close, kind, region) {
            const entry = { close, kind, region };
            stack.current = [...stack.current, entry];
            return () => {
                stack.current = stack.current.filter((e) => e !== entry);
            };
        },
        kinds: () => stack.current.map((e) => e.kind),
    }), []);
    if (outer)
        return _jsx(_Fragment, { children: children });
    return _jsx(LayerContext.Provider, { value: api, children: children });
}
/** Registers an open layer; `close` runs when Escape reaches it. */
export function useLayer(open, close, kind, options = {}) {
    const api = useContext(LayerContext);
    const closeRef = useRef(close);
    closeRef.current = close;
    const { region } = options;
    useEffect(() => {
        if (!open || !api)
            return;
        return api.register(() => closeRef.current(), kind, region);
    }, [open, api, kind, region]);
}
/** Whether a `LayerProvider` is above (a layer without one handles its own Esc). */
export function useHasLayerProvider() {
    return useContext(LayerContext) !== null;
}
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
/** The focusable elements inside `root`, in document order. */
export function focusableIn(root) {
    return [...root.querySelectorAll(FOCUSABLE)].filter((el) => !el.hasAttribute('disabled'));
}
/**
 * A keydown handler that keeps Tab and Shift-Tab cycling inside `ref`'s
 * element (a modal layer: the center peek, `Dialog`).
 */
export function useFocusTrap(ref) {
    return useCallback((event) => {
        const root = ref.current;
        if (event.key !== 'Tab' || !root)
            return;
        const focusable = focusableIn(root);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || !last) {
            event.preventDefault();
            return;
        }
        const active = document.activeElement;
        if (event.shiftKey && (active === first || active === root)) {
            event.preventDefault();
            last.focus();
        }
        else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    }, [ref]);
}
/** The element a closing layer last gave focus back to, until that focus leaves it. */
let restoredFocus = null;
/**
 * Whether `el` holds focus that a closing layer gave back: a hover card does
 * not open for it (a card is a layer, so it would take the next Esc).
 */
export function hasRestoredFocus(el) {
    return el !== null && el === restoredFocus;
}
/**
 * Remembers the element that had focus when `open` became true and gives it
 * focus back when `open` becomes false (or the layer unmounts).
 */
export function useReturnFocus(open, trigger) {
    const saved = useRef(null);
    useEffect(() => {
        if (!open)
            return;
        // A ref is resolved here, at open, so a trigger that mounts with the layer is found.
        const target = trigger && 'current' in trigger ? trigger.current : trigger;
        saved.current =
            target ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
        return () => {
            const el = saved.current;
            if (!el?.isConnected)
                return;
            restoredFocus = el;
            el.addEventListener('blur', () => {
                if (restoredFocus === el)
                    restoredFocus = null;
            }, { once: true });
            el.focus();
        };
    }, [open, trigger]);
}
/* ------------------------------------------------------------------ */
/* media queries                                                       */
/* ------------------------------------------------------------------ */
export function useMediaQuery(query) {
    const [matches, setMatches] = useState(() => typeof window === 'undefined' ? false : window.matchMedia(query).matches);
    useEffect(() => {
        const mql = window.matchMedia(query);
        const on = () => setMatches(mql.matches);
        on();
        mql.addEventListener('change', on);
        return () => mql.removeEventListener('change', on);
    }, [query]);
    return matches;
}
/** Whether the platform shows ⌘ and ⌥ (Apple) rather than Ctrl and Alt. */
export function isApplePlatform() {
    if (typeof navigator === 'undefined')
        return false;
    return /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);
}
/** Mod is ⌘ on Apple platforms and Ctrl elsewhere. */
export function hasMod(event) {
    return isApplePlatform() ? event.metaKey : event.ctrlKey;
}
//# sourceMappingURL=infra.js.map